import React, { useState, useEffect } from "react";
import { api } from "./axiosInstance"; // ✅ Shortened import
import SmsEnrollmentFlow from "./SmsEnrollmentFlow";

const MfaSetup = ({ onComplete }) => {
    const [step, setStep] = useState('select'); // 'select' | 'totp' | 'sms'
    const [hoveredMethod, setHoveredMethod] = useState(null);
    const [qrCode, setQrCode] = useState("");
    const [token, setToken] = useState("");
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (step !== 'totp') return;
        const fetchQrCode = async () => {
            setLoading(true);
            try {
                const response = await api.post("/mfa/generate");
                setQrCode(response.data.qrCodeUrl);
                setLoading(false);
            } catch (err) {
                console.error("Error generating QR code:", err);
                setError("Failed to load QR code.");
                setLoading(false);
            }
        };
        fetchQrCode();
    }, [step]);

    const handleVerify = async () => {
        try {
            setError("");
            const response = await api.post("/mfa/verify", { token });

            // ✅ Capture recovery codes from response
            if (response.data.recoveryCodes) {
                setRecoveryCodes(response.data.recoveryCodes);
            } else {
                // If no codes returned (legacy), just close
                setTimeout(() => { if (onComplete) onComplete(); }, 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || "Invalid Code. Try again.");
        }
    };

    // Method Selector
    if (step === 'select') {
        return (
            <div className="text-center">
                <h2 className="text-xl font-bold text-orange mb-4">Choose your MFA method</h2>
                <div className="flex gap-4 justify-center mt-4">
                    <button
                        onClick={() => setStep('totp')}
                        onMouseEnter={() => setHoveredMethod('totp')}
                        onMouseLeave={() => setHoveredMethod(null)}
                        className={`flex-1 p-6 rounded-lg border-2 cursor-pointer text-left transition-colors ${
                            hoveredMethod === 'totp' ? 'border-orange bg-bluegray' : 'border-lightblue bg-bluegray'
                        }`}
                    >
                        <h3 className="text-lg font-bold text-lightgray mb-1">Authenticator App</h3>
                        <p className="text-sm text-lightgray opacity-75">Use Google Authenticator or similar app</p>
                    </button>
                    <button
                        onClick={() => setStep('sms')}
                        onMouseEnter={() => setHoveredMethod('sms')}
                        onMouseLeave={() => setHoveredMethod(null)}
                        className={`flex-1 p-6 rounded-lg border-2 cursor-pointer text-left transition-colors ${
                            hoveredMethod === 'sms' ? 'border-orange bg-bluegray' : 'border-lightblue bg-bluegray'
                        }`}
                    >
                        <h3 className="text-lg font-bold text-lightgray mb-1">Text Message</h3>
                        <p className="text-sm text-lightgray opacity-75">Receive a 6-digit code by SMS</p>
                    </button>
                </div>
            </div>
        );
    }

    // SMS Branch
    if (step === 'sms') {
        return (
            <div className="text-center">
                <SmsEnrollmentFlow onBack={() => setStep('select')} onComplete={onComplete} />
            </div>
        );
    }

    // TOTP Branch (step === 'totp')
    return (
        <div className="text-center">
            <button onClick={() => setStep('select')} className="text-lightblue hover:text-white text-sm mb-4">
                &larr; Back to method selection
            </button>

            <h2 className="text-xl font-bold text-orange mb-4">Setup Multi-Factor Authentication</h2>

            {/* ✅ VIEW 1: Show QR Code (Before Success) */}
            {!recoveryCodes.length ? (
                <>
                    {loading ? (
                        <p className="text-white">Loading QR Code...</p>
                    ) : error ? (
                        <p className="text-red-500">{error}</p>
                    ) : (
                        <>
                            <p className="text-gray-300 mb-4 text-sm">
                                Scan this QR code with your authenticator app, then enter the 6-digit code below.
                            </p>
                            {qrCode && <img src={qrCode} alt="MFA QR Code" className="mx-auto mb-4 border-4 border-white rounded-lg" />}

                            <input
                                type="text"
                                placeholder="Enter 6-digit code"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                className="w-full p-2 mb-3 rounded bg-deepblue text-white border border-lightblue text-center text-lg tracking-widest"
                                maxLength="6"
                            />

                            <button
                                onClick={handleVerify}
                                className="w-full py-2 bg-orange text-white font-bold rounded hover:bg-deeporange transition"
                            >
                                Verify & Enable
                            </button>
                        </>
                    )}
                </>
            ) : (
                // ✅ VIEW 2: Show Recovery Codes (After Success)
                <div className="bg-bluegray p-4 rounded-lg">
                    <p className="text-green-400 font-bold mb-2">MFA Enabled!</p>
                    <p className="text-red-400 font-bold text-sm mb-2">IMPORTANT: Save these backup codes. If you lose your device, these are the only way to access your account.</p>

                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {recoveryCodes.map((code, index) => (
                            <div key={index} className="bg-deepblue p-2 text-white font-mono text-center rounded border border-lightblue">
                                {code}
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={() => { if (onComplete) onComplete(); }}
                        className="w-full py-2 bg-orange text-white font-bold rounded hover:bg-deeporange transition"
                    >
                        I have saved them
                    </button>
                </div>
            )}
        </div>
    );
};

export default MfaSetup;
